"use client";

import { HelpCircleIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";

function FaqCard({ question, answer }: { question: string; answer: React.ReactNode }) {
  return (
    <Card className="group relative h-full overflow-hidden border border-orange-200/50 bg-white/95 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
        aria-hidden="true"
      />
      <CardContent className="relative flex h-full flex-col p-6">
        <h3 className="mb-3 flex items-start gap-x-3 text-lg font-semibold text-gray-900 dark:text-white">
          <span className="flex h-[1lh] items-center">
            <HelpCircleIcon className="size-5 shrink-0 text-orange-500 dark:text-orange-400" />
          </span>
          {question}
        </h3>
        <div className="text-base leading-relaxed text-gray-600 dark:text-gray-300">{answer}</div>
      </CardContent>
    </Card>
  );
}

const faqItems: { question: string; answer: React.ReactNode }[] = [
  {
    question: "Should I start with one-off generation or connect a domain?",
    answer: (
      <>
        Start with one-off generation if you need a quick preview for a single link. Connect a domain when you want
        autopilot coverage across many pages, daily refreshes, and analytics in one workspace.
      </>
    ),
  },
  {
    question: "What happens after I connect a domain?",
    answer: (
      <>
        OG Pilot verifies domain ownership, pulls your brand signals (like colors and logo), and you can start serving
        branded previews for your pages using our API. You can use no-code guides or SDKs for programmatic control.
      </>
    ),
  },
  {
    question: "How often do connected OG images refresh?",
    answer: (
      <>
        Once they&apos;re created, they never expire by default. But you can optionally configure them to refresh every
        24 hours. Some social platforms cache previews, so externally visible updates can appear after their cache
        recrawls.
      </>
    ),
  },
  {
    question: "How does domain verification work?",
    answer: (
      <>
        We provide a DNS TXT record (for example,{" "}
        <span className="font-semibold text-gray-900 dark:text-white">_ogpilot-verification</span> with{" "}
        <span className="font-semibold text-gray-900 dark:text-white">ogpilot=...</span>). Once DNS propagates, we
        confirm ownership and unlock the domain for use.
      </>
    ),
  },
  {
    question: "Is there a free trial?",
    answer: <>Yes. Every plan starts with a 7-day free trial, and there&apos;s no credit card required to start.</>,
  },
  {
    question: "How do analytics work?",
    answer: (
      <>
        We track shares over time, top-performing pages, and platform mix. Analytics are grouped by domain so you can
        measure what&apos;s working across connected properties.
      </>
    ),
  },
  {
    question: "What happens if I go past the usage limit?",
    answer: (
      <>
        You can upgrade to a higher tier or reach out for a custom plan. Usage remains visible so you can make the
        change before it impacts your workflow.
      </>
    ),
  },
  {
    question: "What integrations are available today?",
    answer: (
      <>
        Live guides are available for Carrd and WordPress. Shopify and Webflow integrations are in progress, and you can
        use our Ruby, JavaScript, Python, or Laravel/PHP SDKs for custom stacks right now.
      </>
    ),
  },
  {
    question: "How is API access secured?",
    answer: (
      <>
        API requests are signed with JWTs tied to your API key and verified domain. We validate issuers and enforce
        controls like domain checks and rate limits to protect your traffic.
      </>
    ),
  },
  {
    question: "What analytics can we track?",
    answer: (
      <>
        Track shares over time, top-performing pages, platform mix, and referral/device trends. Analytics are grouped by
        domain so teams can measure what&apos;s working across connected properties.
      </>
    ),
  },
  {
    question: "How quickly can I implement OG Pilot?",
    answer: (
      <>
        Most users generate their first result in minutes. DNS verification timing varies by provider, but rollout is
        usually fast once the TXT record is live.
      </>
    ),
  },
];

export default function FaqSection() {
  return (
    <section
      id="faq"
      className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-100/50 py-16 sm:py-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="space-y-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase dark:text-orange-300">
              QUESTIONS BEFORE YOU CONNECT
            </p>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Answers for one-off and domain-connected social previews
            </h2>
            <p className="text-lg leading-relaxed text-balance text-gray-600 dark:text-gray-300">
              Everything you need to choose the right setup and launch with confidence. If you need additional details,{" "}
              <a
                href="mailto:support@ogpilot.com"
                className="text-orange-600 underline-offset-2 hover:text-orange-700 hover:underline dark:text-orange-400 dark:hover:text-orange-300"
              >
                contact our team
              </a>
              .
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {faqItems.map((item) => (
              <FaqCard key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
